/**
 * App Controller - Manages authentication and app initialization
 *
 * Handles:
 * - Auth state management
 * - Database initialization for authenticated users
 * - Sync service initialization
 * - Real-time notifications (SSE)
 * - Background sync monitoring
 */

import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import { authService, type AuthState } from "../services/auth";
import { syncService } from "../services/sync";
import { getBackgroundSyncService } from "../services/sync/background-sync";
import { dbManager } from "../db/user-database";
import { Router } from "@vaadin/router";
import "@awesome.me/webawesome/dist/components/spinner/spinner.js";
import "@awesome.me/webawesome/dist/components/callout/callout.js";
import "@awesome.me/webawesome/dist/components/icon/icon.js";

import "../pages/login-page";
import "../pages/help-page";

// SSE Notifications
import {
  SSEClient,
  SseHub,
  toast,
  isSyncRequiredEvent,
  isSyncDataChangedEvent,
  isRelationshipRequestedEvent,
  isRelationshipAcceptedEvent,
  isSupportResponseAddedEvent,
} from "../services/notifications";

@customElement("app-controller")
export class AppController extends LitElement {
  @state() private authState: AuthState = {
    isAuthenticated: false,
    isLoading: true,
  };
  @state() private isInitializing = true;
  @state() private initError = "";
  @state() private isSyncing = false;
  @state() private syncMessage = "Syncing your data...";
  @state() private showGuestHelp = false;

  // Cleanup functions for SSE subscriptions
  private _sseUnsubscribers: Array<() => void> = [];
  private _bgSyncUnsubscribers: Array<() => void> = [];
  private _syncInitUnsubscribe?: () => void;

  static styles = css`
    :host {
      display: block;
      min-height: 100vh;
    }

    .loading-screen {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
    }

    .loading-screen wa-spinner {
      font-size: 2rem;
      --indicator-color: white;
      --track-color: rgba(255, 255, 255, 0.3);
    }

    .loading-screen p {
      margin-top: 1rem;
      font-size: 1rem;
    }

    .error-screen {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      background: #f8d7da;
      color: #721c24;
      padding: 2rem;
      text-align: center;
    }

    .error-screen h2 {
      margin: 0 0 1rem 0;
    }

    .error-screen button {
      margin-top: 1rem;
      padding: 0.5rem 1rem;
      background: #0d6efd;
      color: white;
      border: none;
      border-radius: 0.25rem;
      cursor: pointer;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this.initializeSSE();
    this.initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.cleanupSSE();
    this.cleanupBackgroundSync();
  }

  /**
   * Initialize SSE client (once, before auth).
   * Connection mode depends on auth state.
   */
  private initializeSSE() {
    if (SSEClient.isInitialized) return;

    SSEClient.initialize({
      onlineCallback: (online) => {
        console.log(
          `[AppController] SSE ${online ? "connected" : "disconnected"}`,
        );
      },
      isLoggedIn: () => this.authState.isAuthenticated,
      afterOnlineEvent: () => {
        // Re-establish subscriptions after reconnect
        if (this.authState.isAuthenticated && this.authState.userId) {
          this.setupUserSubscriptions(this.authState.userId);
        }
      },
    });

    SseHub.initialize();

    // Connect in anonymous mode initially
    SSEClient.instance.connectAppropriate();
  }

  /**
   * Clean up SSE subscriptions.
   */
  private cleanupSSE() {
    this._sseUnsubscribers.forEach((unsub) => unsub());
    this._sseUnsubscribers = [];
  }

  /**
   * Clean up background sync listeners.
   */
  private cleanupBackgroundSync() {
    this._bgSyncUnsubscribers.forEach((unsub) => unsub());
    this._bgSyncUnsubscribers = [];
  }

  /**
   * Set up SSE subscriptions for the authenticated user.
   */
  private setupUserSubscriptions(userId: string) {
    // Clean up any existing subscriptions first
    this.cleanupSSE();

    const hub = SseHub.instance;

    // Subscribe to user-specific events
    const userUnsub = hub.subscribeUser(userId, (evt) => {
      // Sync notifications
      if (isSyncRequiredEvent(evt.data) || isSyncDataChangedEvent(evt.data)) {
        toast.info("New data available - refreshing...");
        // Trigger sync refresh
        syncService.sync().catch(console.error);
      }

      // Relationship requests
      if (isRelationshipRequestedEvent(evt.data)) {
        toast.info("You have a new relationship request");
      }

      if (isRelationshipAcceptedEvent(evt.data)) {
        toast.success("Relationship request accepted!");
      }
    });
    this._sseUnsubscribers.push(userUnsub);

    // Subscribe to patient-specific support events (for responses from clinicians)
    const supportUnsub = hub.subscribePatientSupport(userId, (evt) => {
      if (isSupportResponseAddedEvent(evt.data)) {
        toast.info("You have a new response to your support request");
      }
    });
    this._sseUnsubscribers.push(supportUnsub);

    console.log(
      "[AppController] SSE subscriptions established for user:",
      userId,
    );
  }

  /**
   * Set up background sync event listeners.
   */
  private setupBackgroundSync() {
    this.cleanupBackgroundSync();

    const bgSync = getBackgroundSyncService();

    // Listen for sync completions
    const completeUnsub = bgSync.onSyncComplete((tag) => {
      console.log("[AppController] Background sync completed:", tag);

      if (tag === "sync-entries") {
        toast.success("Entries synced");
        // Refresh sync service data
        syncService.sync().catch(console.error);
      } else if (tag === "sync-cgm") {
        toast.success("CGM data synced");
      } else if (tag === "sync-support") {
        toast.success("Support request submitted");
      }
    });
    this._bgSyncUnsubscribers.push(completeUnsub);

    // Listen for sync failures
    const failedUnsub = bgSync.onSyncFailed((tag, error) => {
      console.error("[AppController] Background sync failed:", tag, error);
      toast.warning(`Sync pending - will retry when online`);
    });
    this._bgSyncUnsubscribers.push(failedUnsub);
  }

  private async initialize() {
    try {
      // Subscribe to auth state changes
      authService.subscribe((state) => {
        this.authState = state;
        this.handleAuthStateChange(state);
      });

      // Initialize auth service (checks for existing session)
      await authService.initialize();
      this.isInitializing = false;
    } catch (error) {
      console.error("Initialization failed:", error);
      this.initError =
        error instanceof Error ? error.message : "Initialization failed";
      this.isInitializing = false;
    }
  }

  private async handleAuthStateChange(state: AuthState) {
    if (state.isAuthenticated && state.userId) {
      // User logged in - initialize their database and sync
      try {
        // Show syncing screen while we set up
        this.isSyncing = true;
        this.syncMessage = "Setting up your database...";

        await dbManager.switchToUserDatabase(state.userId);

        this.syncMessage = "Syncing your data...";

        // Initialize sync service (this will pull latest data from server)
        await syncService.initialize();

        // Wait for initial sync to complete (or timeout)
        await this._waitForInitialSyncComplete();

        // Set up real-time notifications
        SSEClient.instance.connectAppropriate(); // Switch to authenticated mode
        this.setupUserSubscriptions(state.userId);
        this.setupBackgroundSync();

        // Done syncing - show main app
        this.isSyncing = false;

        // Check for pending invite from accept-invite-page
        const pendingInvite = sessionStorage.getItem("pendingInvite");
        if (pendingInvite) {
          sessionStorage.removeItem("pendingInvite");
          // Small delay to ensure router is ready
          requestAnimationFrame(() => {
            Router.go(`/invite/${pendingInvite}`);
          });
        }
      } catch (error) {
        console.error("Failed to initialize user database:", error);
        this.isSyncing = false;
      }
    } else if (!state.isAuthenticated && !state.isLoading) {
      // User logged out - cleanup
      this.isSyncing = false;
      this._syncInitUnsubscribe?.();
      this.cleanupSSE();
      this.cleanupBackgroundSync();
      SSEClient.instance.connectAppropriate(); // Switch back to anonymous mode

      syncService.cleanup();
      await dbManager.close();
    }
  }

  /**
   * Wait for initial sync to complete with timeout.
   * This ensures we have the latest data before showing the app.
   */
  private async _waitForInitialSyncComplete(): Promise<void> {
    const currentState = syncService.state;

    // Already complete
    if (currentState.initialSyncComplete || currentState.isConfirmedFreshUser) {
      return;
    }

    // Offline - proceed with local data
    if (!navigator.onLine || currentState.status === "offline") {
      return;
    }

    return new Promise((resolve) => {
      // Timeout after 20 seconds - let user proceed with whatever data we have
      const timeout = setTimeout(() => {
        console.warn("[AppController] Initial sync timeout - proceeding");
        this._syncInitUnsubscribe?.();
        this._syncInitUnsubscribe = undefined;
        resolve();
      }, 20000);

      this._syncInitUnsubscribe = syncService.subscribe((syncState) => {
        // Update message based on sync status
        if (syncState.status === "syncing") {
          this.syncMessage = "Downloading your data...";
        } else if (syncState.status === "checking") {
          this.syncMessage = "Checking for updates...";
        }

        if (
          syncState.initialSyncComplete ||
          syncState.isConfirmedFreshUser ||
          syncState.status === "error"
        ) {
          clearTimeout(timeout);
          this._syncInitUnsubscribe?.();
          this._syncInitUnsubscribe = undefined;
          resolve();
        }
      });
    });
  }

  private handleAuthSuccess() {
    // Auth state change will be handled by subscription
  }

  private handleRetry() {
    this.isInitializing = true;
    this.initError = "";
    this.initialize();
  }

  private handleShowGuestHelp() {
    this.showGuestHelp = true;
  }

  private handleBackToLogin() {
    this.showGuestHelp = false;
  }

  render() {
    // Show loading screen during initialization
    if (this.isInitializing) {
      return html`
        <div class="loading-screen">
          <wa-spinner></wa-spinner>
          <p>Loading DAFNE Log...</p>
        </div>
      `;
    }

    // Show error screen if initialization failed
    if (this.initError) {
      return html`
        <div class="error-screen">
          <h2>Something went wrong</h2>
          <p>${this.initError}</p>
          <button @click=${this.handleRetry}>Try Again</button>
        </div>
      `;
    }

    // Show login page if not authenticated
    if (!this.authState.isAuthenticated) {
      if (this.showGuestHelp) {
        return html`<help-page
          guest-mode
          @back-to-login=${this.handleBackToLogin}
        ></help-page>`;
      }
      return html`<login-page
        @auth-success=${this.handleAuthSuccess}
        @show-help=${this.handleShowGuestHelp}
      ></login-page>`;
    }

    // Show syncing screen while initial sync completes
    if (this.isSyncing) {
      return html`
        <div class="loading-screen">
          <wa-spinner></wa-spinner>
          <p>${this.syncMessage}</p>
        </div>
      `;
    }

    // Show main app
    return html`<slot></slot>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "app-controller": AppController;
  }
}
